// 後台「EXE 使用狀況」—— 誰在用精靈 / AI 策略助理、跑哪支策略
//   資料來自 exesession（每次 EXE 啟動登入時 upsert）。
//   ⚠️「最後啟動」不是即時在線：精靈心跳(strategy-health)不帶使用者身分、也刻意不記錄，
//      所以這裡以「最近一次 EXE 啟動時間」近似判斷誰在使用。
import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { TextField, MenuItem, Box } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { fmtDateTime } from "utils/datetime";
import { COLORS } from "theme/tokens";
import * as XLSX from "xlsx";

const KIND_LABEL = { GENIE: "策略精靈", ASSISTANT: "AI 策略助理", SOFT: "桌面程式", UNKNOWN: "未知" };
const PAGE_SIZE = 50;

function StatTile({ label, value }) {
  return (
    <Card sx={{ p: 2.5, textAlign: "center", height: "100%" }}>
      <MDTypography variant="h3" fontWeight="bold" color="info">{value ?? "—"}</MDTypography>
      <MDTypography variant="button" color="text">{label}</MDTypography>
    </Card>
  );
}

export default function ExeSessions() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState("all");
  const [activeHours, setActiveHours] = useState("24");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetchData = async (p = 1) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ kind, activeHours, search, page: String(p), limit: String(PAGE_SIZE) });
      const res = await fetch(`/api/admin/exe-sessions?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) {
        setData(d);
        setPage(d.page || p);
      } else {
        setData(null);
        window.alert(d?.message || "讀取失敗");
      }
    } catch (e) {
      setData(null);
      console.error("fetch exe-sessions error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleExport = () => {
    const rows = (data?.items || []).map((r) => ({
      使用者: r.email,
      姓名: r.fullName || "",
      方案: r.planCode || "",
      類型: KIND_LABEL[r.kind] || r.kind,
      策略: r.strategyName || "",
      商品: r.symbol || "",
      策略ID: r.strategyId,
      電腦: r.device || "",
      EXE版本: r.exeVersion || "",
      啟動次數: r.loginCount,
      最後啟動: fmtDateTime(r.lastSeenAt),
      首次啟動: fmtDateTime(r.firstSeenAt),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "EXE使用狀況");
    XLSX.writeFile(wb, `exe_sessions_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const items = data?.items || [];
  const totalPages = Math.max(1, data?.totalPages || 1);
  const th = { py: 1, pr: 2, whiteSpace: "nowrap" };
  const td = { py: 1, pr: 2, whiteSpace: "nowrap" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} mb={1}>
          <Grid item xs={12} md={4}>
            <StatTile label="近 24 小時啟動過的使用者" value={data?.summary?.active24hUsers} />
          </Grid>
          <Grid item xs={6} md={4}>
            <StatTile label="其中使用策略精靈" value={data?.summary?.genieUsers} />
          </Grid>
          <Grid item xs={6} md={4}>
            <StatTile label="其中使用 AI 策略助理" value={data?.summary?.assistantUsers} />
          </Grid>
        </Grid>

        <Card sx={{ p: 2, mt: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={2}>
            <MDTypography variant="h5" fontWeight="bold">EXE 使用狀況</MDTypography>
            <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
              <TextField select label="類型" size="small" sx={{ minWidth: 140 }}
                value={kind} onChange={(e) => setKind(e.target.value)}>
                <MenuItem value="all">全部</MenuItem>
                <MenuItem value="GENIE">策略精靈</MenuItem>
                <MenuItem value="ASSISTANT">AI 策略助理</MenuItem>
                <MenuItem value="SOFT">桌面程式</MenuItem>
              </TextField>
              <TextField select label="最後啟動" size="small" sx={{ minWidth: 140 }}
                value={activeHours} onChange={(e) => setActiveHours(e.target.value)}>
                <MenuItem value="24">24 小時內</MenuItem>
                <MenuItem value="168">7 天內</MenuItem>
                <MenuItem value="720">30 天內</MenuItem>
                <MenuItem value="0">不限</MenuItem>
              </TextField>
              <MDInput label="搜尋使用者..." size="small" value={search} onChange={(e) => setSearch(e.target.value)} />
              <MDButton color="info" variant="gradient" size="small" onClick={() => fetchData(1)} disabled={loading}>
                {loading ? "查詢中..." : "查詢"}
              </MDButton>
              <MDButton color="dark" variant="outlined" size="small" onClick={handleExport} disabled={!items.length}>
                匯出 Excel
              </MDButton>
            </Box>
          </Box>

          {loading ? (
            <MDTypography variant="button" color="text">讀取中...</MDTypography>
          ) : items.length === 0 ? (
            <MDTypography variant="button" color="text">此條件下查無紀錄</MDTypography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ textAlign: "left", color: "text.secondary" }}>
                    <Box component="th" sx={th}>使用者</Box>
                    <Box component="th" sx={th}>類型</Box>
                    <Box component="th" sx={th}>策略</Box>
                    <Box component="th" sx={th}>商品</Box>
                    <Box component="th" sx={th}>方案</Box>
                    <Box component="th" sx={th}>電腦</Box>
                    <Box component="th" sx={th}>啟動次數</Box>
                    <Box component="th" sx={{ py: 1, whiteSpace: "nowrap" }}>最後啟動</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {items.map((r) => (
                    <Box component="tr" key={r.id} sx={{ borderTop: "1px solid #eef0f3" }}>
                      <Box component="td" sx={td} title={r.fullName || ""}>{r.email}</Box>
                      <Box component="td"
                        sx={{ ...td, fontWeight: 600, color: r.kind === "GENIE" ? COLORS.main : COLORS.grayDark }}>
                        {KIND_LABEL[r.kind] || r.kind}
                      </Box>
                      <Box component="td"
                        sx={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
                        title={r.strategyId}>
                        {r.strategyName || r.strategyId}
                      </Box>
                      <Box component="td" sx={td}>{r.symbol || "—"}</Box>
                      <Box component="td" sx={td}>{r.planCode || "—"}</Box>
                      <Box component="td"
                        sx={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}
                        title={r.device || ""}>
                        {r.device || "—"}
                      </Box>
                      <Box component="td" sx={td}>{r.loginCount}</Box>
                      <Box component="td" sx={{ py: 1, whiteSpace: "nowrap" }}>
                        {fmtDateTime(r.lastSeenAt, { withSeconds: true })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <MDTypography variant="caption" color="text">
              共 {data?.total ?? 0} 筆 · 第 {page} / {totalPages} 頁
            </MDTypography>
            <Box display="flex" gap={1}>
              <MDButton size="small" color="secondary" disabled={loading || page <= 1}
                onClick={() => fetchData(page - 1)}>上一頁</MDButton>
              <MDButton size="small" color="secondary" disabled={loading || page >= totalPages}
                onClick={() => fetchData(page + 1)}>下一頁</MDButton>
            </Box>
          </Box>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
